<template>
    <div id="page-container" class="p-d-flex p-flex-column" :style="fullscreen ? {backgroundColor: 'rgb(231,228,219)'} : {}">

        <ProgressSpinner v-if="loading"
                         stroke-width="1"
                         style="position: fixed; width: 250px; height: 250px; top: 30%; left: 50%;"/>

        <Timer :every="30" @times-up="viewIndex = (viewIndex + 1) % views.length"></Timer>

        <div class="p-d-flex p-jc-between p-ai-center p-p-3">
            <h1 :style="headerStyle">
                {{ $t('sustainabilityOverview') }}
            </h1>

            <template v-if="fullscreen">
                <img :src="chooseGif" alt="" width="125px">
            </template>
            <span v-else class="p-link">
                <span class="pi pi-window-maximize" @click="toggleFullScreen"></span>
            </span>
        </div>

        <div v-if="!fullscreen || views[viewIndex] === 'electricity'" class="p-d-flex p-jc-between p-ai-center p-px-lg-2 p-px-xl-6" style="height: 0; flex: 1">
            <div class="p-col-12 p-lg-8 p-xl-8">
                <Chart type="bar" :data="electricityBarChartData" :options="electricityConsumptionBarChartOptions"
                       :style="chartBackground"/> 
            </div>

            <div class="p-col-12 p-sm-10 p-lg-4">
                <EnergyConsumptionDataTable
                    :data="electricityTableData"
                    :base-year="mostRecentButOneCompleteYear"
                    :target-year="mostRecentCompleteYear"
                    :header="$t('electricityDevelopment')"
                    groupRowsBy="grouped"
                    :fullscreen="fullscreen">
                </EnergyConsumptionDataTable>
            </div>
        </div>

        <div v-if="!fullscreen || views[viewIndex] === 'heating'" class="p-d-flex p-jc-between p-ai-center p-px-lg-2 p-px-xl-6" style="height: 0; flex: 1">
            <div class="p-col-12 p-lg-8 p-xl-8">
                <Chart type="bar" :data="heatingBarChartData" :options="heatConsumptionBarChartOptions"
                       :style="chartBackground"/>
            </div>

            <div class="p-col-12 p-sm-10 p-lg-4">
                <EnergyConsumptionDataTable
                    :data="heatingTableData"
                    :base-year="mostRecentButOneCompleteYear"
                    :target-year="mostRecentCompleteYear"
                    :header="$t('heatingDevelopment')"
                    groupRowsBy="grouped"
                    :fullscreen="fullscreen">
                </EnergyConsumptionDataTable>
            </div>
        </div>
 
        <div v-if="!fullscreen || views[viewIndex] === 'water'" class="p-d-flex p-jc-between p-ai-center p-px-lg-2 p-px-xl-6" style="height: 0; flex: 1">
            <div class="p-col-12 p-xl-8 p-lg-8">
                <Chart type="bar" :data="waterBarChartData" :options="waterBarChartOptions"
                       :style="chartBackground"/>   
            </div>

            <div class="p-col-12 p-sm-10 p-lg-4">
                <EnergyConsumptionDataTable
                    :data="waterTableData"
                    :base-year="mostRecentButOneCompleteYear"
                    :target-year="mostRecentCompleteYear"
                    :header="$t('waterDevelopment')"
                    groupRowsBy="grouped"
                    :fullscreen="fullscreen">
                </EnergyConsumptionDataTable> 
            </div>
        </div>

        <div v-if="!fullscreen || views[viewIndex] === 'disposal'" class="p-d-flex p-jc-center p-ai-center p-px-lg-2 p-px-xl-6" style="height: 0; flex: 1">
            <div class="p-col-12 p-xl-8 p-lg-8">
                <Chart type="bar" indexAxis="y" :data="disposalBarChartData" :options="disposalBarChartOptions"
                       :style="chartBackground"/>
            </div>
            <div class="p-col-12 p-sm-10 p-lg-4">
                <EnergyConsumptionDataTable
                    :data="wasteTableData"
                    :base-year="mostRecentButOneCompleteYear"
                    :target-year="mostRecentCompleteYear"
                    :header="$t('wasteDevelopment')"
                    :fullscreen="fullscreen">
                </EnergyConsumptionDataTable>
            </div>
        </div>

        <div v-if="fullscreen" class="p-col-12 p-d-flex p-jc-between p-ai-end">
            <img :src="logo" alt="" height="100px">
            <span style="font-family: 'BMW'; font-size: 2.5rem">
                {{ $t('dataProvidedBy') }} <strong>m-navigator</strong>
            </span>
        </div>
    </div>
</template>

<script>
import Timer from "@/components/Timer";
import OrganizationService from "@/service/OrganizationService";
import InstallationsService from "@/service/InstallationsService";
import {JwtToken} from "@/utils/jwt";
import EnergyConsumptionDataTable from "@/components/Dashboard/EnergyConsumptionDataTable";

export default {
    components: {
        Timer, EnergyConsumptionDataTable
    },
    data() {
        const currentYear = new Date().getFullYear();

        return {
            loading: true,
            fullscreen: false,
            viewIndex: 0,
            views: ['electricity', 'water', 'heating', 'disposal'],
            currentYear: currentYear.toString(),
            mostRecentCompleteYear: (currentYear - 1).toString(),
            mostRecentButOneCompleteYear: (currentYear - 2).toString(),
            logo: null,
            electricityTableData: null,
            electricityBarChartData: {
                labels: null,
                datasets: null
            },
            electricityConsumptionBarChartOptions: {
                title: {
                    display: true,
                    text: this.$t('monthlyStatistics')
                },
                legend: {
                    position: 'bottom'
                },
                scales: {
                    y: {
                        ticks: {
                            min: 0,
                            callback: function (value) {
                                return value + ' kWh';
                            }
                        }
                    }
                }
            },
            heatConsumptionBarChartOptions: {
                title: {
                    display: true,
                    text: this.$t('monthlyStatistics')
                },
                legend: {
                    position: 'bottom'
                },
                scales: {
                    y: {
                        ticks: {
                            min: 0,
                            callback: function (value) {
                                return value + ' MWh';
                            }
                        }
                    }
                }
            },
            heatingTableData: null,
            heatingBarChartData: {
                labels: null,
                datasets: null
            },
            waterTableData: null,
            waterBarChartData: {
                labels: null,
                datasets: null
            },
            wasteTableData: null,
            wasteBarChartData: {
                labels: null,
                datasets: null
            },
            waterBarChartOptions: {
                title: {
                    display: true,
                    text: this.$t('monthlyStatistics')
                },
                legend: {
                    position: 'bottom'
                },
                scales: {
                    y: {
                        ticks: {
                            min: 0,
                            callback: function (value) {
                                return value + ' m³';
                            }
                        }
                    }
                }
            },
            disposalBarChartData: {
                labels: null,
                datasets: null
            },
            disposalBarChartOptions: {
                title: {
                    display: true,
                    text: this.$t('monthlyStatistics')
                },
                legend: {
                    position: 'bottom'
                },
                scales: {
                    x: {
                        ticks: {
                            min: 0
                        }
                    }
                }
            },
            disposalTableData: null
        };
    },
    created() {
        this.installationsService = new InstallationsService();
        this.organizationService = new OrganizationService();

        this.organizationService.getOrganizationLogo(JwtToken.fromLocalStorage().organizationId())
            .then(logo => this.logo = logo)
            .catch(() => null);
    },
    mounted() {
        Promise.all([
            this.prepareBarChartData(this.installationsService.getMonthlyElectricityConsumption, result => this.electricityBarChartData = result),
            this.prepareElectricityTableData(),
            this.prepareBarChartData(this.installationsService.getMonthlyHeatingConsumption, result => this.heatingBarChartData = result),
            this.prepareHeatingTableData(),
            this.prepareBarChartData(this.installationsService.getMonthlyWaterConsumption, result => this.waterBarChartData = result),
            this.prepareWaterTableData(),
            this.prepareWasteBarChartData(this.installationsService.getYearlyWasteConsumption, result => this.wasteBarChartData = result),
            this.prepareWasteTableData(),
        ]).then(() => this.loading = false);

        document.addEventListener('keydown', this.onF11Key.bind(this));
        document.addEventListener('fullscreenchange', this.onFullScreen.bind(this));
    },
    methods: {
        prepareBarChartData(dataSupplier, resultConsumer) {
            
            return Promise.all([
                dataSupplier(this.mostRecentCompleteYear),
                dataSupplier(this.currentYear)
            ])  
                .then(results => {
                    resultConsumer({
                        labels: this.$t('months'),
                        datasets: [{
                            data: results[0].map(x => Math.round(x*100)/100),
                            label: this.mostRecentCompleteYear,
                            backgroundColor: "#D3DAE5"
                        }, {
                            data: results[1].map(x => Math.round(x*100)/100),
                            label: this.currentYear,
                            backgroundColor: "#92A2BD"
                        }]
                    })
                });    
        },
        prepareElectricityTableData() {
            const lastCompleteYear = this.mostRecentCompleteYear;
            const baseYear = this.mostRecentButOneCompleteYear;

            return this.installationsService.getElectricityDataComparisonBetweenYears(baseYear, lastCompleteYear)
                .then(data => {

                    const row1 = {};
                    row1['type'] = 'Forbrug (kWh)';
                    row1[baseYear] = data[0].consumption;
                    row1[lastCompleteYear] = data[1].consumption;
                    if (data[0].consumption !== 0) {
                        row1["grouped"] = (data[1].consumption / data[0].consumption - 1);
                    }

                    const row2 = {};
                    row2['type'] = 'Emission (T CO₂)';
                    row2[baseYear] = data[0].co2Emissions / 1000.0;
                    row2[lastCompleteYear] = data[1].co2Emissions / 1000.0;
                    row2["grouped"] = row1["grouped"];

                    const row3 = {};
                    row3['type'] = 'Indeks (kWh/m²)';
                    row3[baseYear] = data[0].electricityIndex;
                    row3[lastCompleteYear] = data[1].electricityIndex;
                    row3["grouped"] = row1["grouped"];

                    const row4 = {};
                    row4['type'] = '';
                    row4[baseYear] = data[0].electricityIndexImage;
                    row4[lastCompleteYear] = data[1].electricityIndexImage;

                    this.electricityTableData = [row1, row2, row3, row4];
                });
        },
        prepareHeatingTableData() {
            const lastCompleteYear = this.mostRecentCompleteYear;
            const baseYear = this.mostRecentButOneCompleteYear;

            return this.installationsService.getHeatingDataComparisonBetweenYears(baseYear, lastCompleteYear)
                .then(data => {

                    const row1 = {};
                    row1['type'] = 'Forbrug (MJ)';
                    row1[baseYear] = data[0].consumption;
                    row1[lastCompleteYear] = data[1].consumption;
                    if (data[0].consumption !== 0) {
                        row1["grouped"] = (data[1].consumption / data[0].consumption - 1);
                    }

                    const row2 = {};
                    row2['type'] = 'Korrigeret forbrug (MJ)';
                    row2[baseYear] = data[0].adjustedConsumption;
                    row2[lastCompleteYear] = data[1].adjustedConsumption;
                    row2["grouped"] = row1["grouped"];

                    const row3 = {};
                    row3['type'] = 'Emission (T CO₂)';
                    row3[baseYear] = data[0].co2Emissions / 1000.0;
                    row3[lastCompleteYear] = data[1].co2Emissions / 1000.0;
                    row3["grouped"] = row1["grouped"];

                    const row4 = {};
                    row4['type'] = 'Indeks (MJ/m²)';
                    row4[baseYear] = data[0].heatIndex;
                    row4[lastCompleteYear] = data[1].heatIndex;
                    row4["grouped"] = row1["grouped"];

                    const row5 = {};
                    row5['type'] = '';
                    row5[baseYear] = data[0].heatIndexImage;
                    row5[lastCompleteYear] = data[1].heatIndexImage;
                    
                    this.heatingTableData = [row1, row2, row3, row4, row5];
                });
        },
        prepareWaterTableData() {
            const lastCompleteYear = this.mostRecentCompleteYear;
            const baseYear = this.mostRecentButOneCompleteYear;

            return this.installationsService.getWaterDataComparisonBetweenYears(baseYear, lastCompleteYear)
                .then(data => {

                    const row1 = {};
                    row1['type'] = 'Forbrug (m³)';
                    row1[baseYear] = data[0].consumption;
                    row1[lastCompleteYear] = data[1].consumption;
                    if (data[0].consumption !== 0) {
                        row1["grouped"] = (data[1].consumption / data[0].consumption - 1);
                    }

                    const row2 = {};
                    row2['type'] = 'Antal bilvaske';
                    row2[baseYear] = data[0].carwashCount;
                    row2[lastCompleteYear] = data[1].carwashCount;

                    this.waterTableData = [row1, row2];
                });
        },
        prepareWasteTableData() {
            const lastCompleteYear = parseInt(this.mostRecentCompleteYear);
            const baseYear = parseInt(this.mostRecentButOneCompleteYear);

            return this.installationsService.getWasteDataComparisonBetweenYears(baseYear, lastCompleteYear)
                .then(data => {

                    const labels = [...new Set([...data[0].consumptionByCategory.keys(),...data[1].consumptionByCategory.keys()])].sort();

                    this.wasteTableData = [];

                    labels.forEach(label => {
                        const row = {};
                        row['type'] = label;
                        row[baseYear] = data[0].consumptionByCategory.get(label);
                        row[lastCompleteYear] = data[1].consumptionByCategory.get(label);
                        if (data[0].consumptionByCategory.get(label) && data[0].consumptionByCategory.get(label) !== 0) {
                            row["grouped"] = ((data[1].consumptionByCategory.get(label) || 0) / data[0].consumptionByCategory.get(label) - 1);
                        }

                        this.wasteTableData.push(row);
                    });
                });
        },
        prepareWasteBarChartData(dataSupplier) {
            return Promise.all([
                dataSupplier(parseInt(this.mostRecentCompleteYear)),
                dataSupplier(parseInt(this.currentYear))
            ])
                .then(results => {
                    const labels = [...new Set([...results[0].keys(),...results[1].keys()])].sort();
                    const baseYearDataPoints = new Map();
                    const targetYearDataPoints = new Map();
                    labels.forEach(label => {
                        baseYearDataPoints.set(label, results[0].get(label) || 0);
                        targetYearDataPoints.set(label, results[1].get(label) || 0);
                    })
                    this.disposalBarChartData = {
                        labels: labels,
                        datasets: [{
                            label: this.mostRecentCompleteYear,
                            backgroundColor: '#42A5F5',
                            data: [...baseYearDataPoints.values()]
                        }, {
                            label: this.currentYear,
                            backgroundColor: '#FFA726',
                            data: [...targetYearDataPoints.values()]
                        }]
                    }
                });
        },
        toggleFullScreen() {
            if (this.fullscreen) {
                this.closeFullScreen();
            } else {
                this.openFullScreen();
            }
        },
        onF11Key(e) {
            if (e.key === "F11") {
                e.preventDefault();
                this.toggleFullScreen();
            }
        },
        onFullScreen() {
            this.fullscreen = !this.fullscreen
        },
        openFullScreen() {
            const elem = document.getElementById("page-container");

            if (elem.requestFullscreen) {
                elem.requestFullscreen();
            } else if (elem.mozRequestFullScreen) {
                elem.mozRequestFullScreen();
            } else if (elem.webkitRequestFullscreen) {
                elem.webkitRequestFullscreen();
            } else if (elem.msRequestFullscreen) {
                elem.msRequestFullscreen();
            }
        },
        closeFullScreen() {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        },
        transparentIfNotFullscreen() {
            return this.fullscreen ? 'no-background' : null;
        }
    },
    computed: {
        chartBackground() {
            return this.fullscreen ? {backgroundColor: 'rgb(235, 235, 235)'} : {};
        },
        chooseGif() {
            if (this.views[this.viewIndex] === 'electricity') {
                return 'assets/icons/electricity.gif';
            } else if (this.views[this.viewIndex] === 'disposal') {
                return 'assets/icons/disposal.gif'
            } else if (this.views[this.viewIndex] === 'water') {
                return 'assets/icons/water.gif'
            } else if (this.views[this.viewIndex] === 'heating') {
                return 'assets/icons/heating.gif'
            }

            return undefined;
        },
        headerStyle() {
            const style = {
                textTransform: 'uppercase'
            };

            if (this.fullscreen) {
                style.fontFamily = 'BMW';
                style.fontSize = '2.5rem';
                style.fontWeight = 'bolder';
            }

            return style;
        }
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.onF11Key);
        document.removeEventListener('fullscreenchange', this.onFullScreen);
    }
}
</script>

<style lang="scss">
@font-face {
    font-family: 'BMW';
    src: local('BMW'),
    url('/assets/fonts/BMW/BMWGroupCondensed-Regular.ttf') format("truetype");
}

.no-background {
    background-color: transparent !important;
}

.p-datatable-header {
    background-color: transparent !important;
    font-size: 1.5rem;
}
</style>

<i18n lang="yaml" locale="en">
sustainabilityOverview: Sustainability Overview
monthlyStatistics: Monthly Statistics
disposal: Waste
electricityDevelopment: Development, Electricity
heatingDevelopment: Development, Heating
waterDevelopment: Development, Water
wasteDevelopment: Development, Waste
dataProvidedBy: Data provided by
</i18n>

<i18n lang="yaml" locale="dk">
sustainabilityOverview: Overblik Over Bæredygtighed
monthlyStatistics: Månedsstatistik
disposal: Affald
electricityDevelopment: Udvikling, elforbrug
heatingDevelopment: Udvikling, varme
waterDevelopment: Udvikling, vand
wasteDevelopment: Udvikling, affald
dataProvidedBy: Data fra
quantityMeasurementUnit: Enhed
</i18n>
